import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import Logo from '@/Asset/images/logo-blaxk.webp';
import DancerParentImage from '@/Asset/images/dancer.webp';
import DanceEducatorImage from '@/Asset/images/instructor.webp';
import StudioOwnerImage from '@/Asset/images/studio-owner.webp';
import Toast from '@/utils/toastManager';
import EastIcon from '@mui/icons-material/East';
import StepIndicator from '../StepIndicator/StepIndicator';
import './Step1.css';

const Step1 = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, updateState } = useOnboardingState();
	const planFromQuery = new URLSearchParams(location.search).get('plan');
	const isRetake = new URLSearchParams(location.search).get('retake') === 'true' || location.state?.isRetake === true;
	const isStudioAccount =
		state.selectedPlanType === 'studio' ||
		state.userType === 'studio-owner' ||
		location.state?.planType === 'studio' ||
		planFromQuery === 'studio';
	const totalSteps = isStudioAccount ? (isRetake ? 5 : 6) : 4;
	const stepItems = Array.from({ length: totalSteps }, (_, index) => index + 1);
	const roleOptions = useMemo(() => {
		const standardOrPlus = [
			{
				id: 'dancer-parent',
				label: 'Dancer / Parent',
				image: DancerParentImage
			},
			{
				id: 'dance-educator',
				label: 'Dance Educator',
				image: DanceEducatorImage
			},
			{
				id: 'studio-owner',
				label: 'Studio Owner',
				image: StudioOwnerImage
			}
		];

		const studioRoles = [
			{
				id: 'studio-owner',
				label: 'Studio Owner',
				image: StudioOwnerImage
			},
			{
				id: 'team-studio-director',
				label: 'Team / Studio Director',
				image: DanceEducatorImage
			},
			{
				id: 'studio-administrator',
				label: 'Studio Administrator',
				image: DancerParentImage
			}
		];

		return isStudioAccount ? studioRoles : standardOrPlus;
	}, [isStudioAccount]);

	const [selectedRole, setSelectedRole] = useState(null);

	useEffect(() => {
		if (isStudioAccount && state.selectedPlanType !== 'studio') {
			updateState({
				selectedPlanType: 'studio',
				userType: location.state?.userType || state.userType || 'studio-owner'
			});
		}
		if (state.onboardingRole) {
			setSelectedRole(state.onboardingRole);
		}
	}, [isStudioAccount, location.state?.userType, state.onboardingRole, state.selectedPlanType, state.userType, updateState]);

	// Validation function for StepIndicator - checks if current step is valid
	const validateStep = () => {
		if (!selectedRole) {
			Toast.error('Please select a role to continue.');
			return false;
		}
		return true;
	};

	// Handle validation failure for StepIndicator
	const handleValidationFail = () => {
		// Toast is already shown in validateStep
	};

	const handleContinue = async () => {
		// Validate that a role is selected
		if (!selectedRole) {
			Toast.error('Please select a role to continue.');
			return;
		}

		const onboardingPreferences = {
			...(state.onboardingPreferences || {}),
			onboardingRole: selectedRole
		};

		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingRole: selectedRole,
				onboardingStep: 5,
				onboardingPreferences
			});

			window?.fbq?.('trackCustom', 'OnboardingV2_Step1Completed', {
				CustomEvent: 'OnboardingV2_Step1Completed',
				role: selectedRole
			});

			updateState({
				currentStep: 5,
				onboardingRole: selectedRole,
				onboardingPreferences
			});

			// Pass plan info and role to next step
			const params = new URLSearchParams(location.search);
			params.set('plan', state.selectedPlanType || planFromQuery || 'standard');
			params.set('billing', state.selectedBillingCycle || params.get('billing') || 'yearly');
			params.set('planId', state.selectedPlanId || params.get('planId') || '');
			params.set('role', selectedRole);
			navigate(`/onboarding/step-2?${params.toString()}`);
		} catch (error) {
			console.error('Failed to save onboarding data:', error);
			Toast.error('Failed to save. Please try again.');
		}
	};

	const handleSkip = async () => {
		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 5
			});
		} catch (error) {
			Toast.error('Failed to save. Please try again.');
			return;
		}

		updateState({ currentStep: 5 });
		navigate('/');
	};

	return (
		<div className="onboarding-step-one">
			<div className="onboarding-step-one__grid-overlay" />
			<div className="onboarding-step-one__content">
				<header className="onboarding-step-one__header">
					<div className="onboarding-step-one__brand-row">
						<button
							type="button"
							className="onboarding-step-one__back-btn"
							onClick={() => {
								if (location.state?.from === 'account' || sessionStorage.getItem('onboarding_entry') === 'account') {
									sessionStorage.removeItem('onboarding_entry');
									navigate('/account');
									return;
								}
								navigate(`/onboarding/welcome?${new URLSearchParams(location.search).toString()}`);
							}}
						>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-step-one__logo" />
					</div>

					<StepIndicator 
						totalSteps={totalSteps} 
						currentStep={1}
						validateStep={validateStep}
						onValidationFail={handleValidationFail}
						basePath="/onboarding"
					/>
				</header>

				<main className="onboarding-step-one__main">
					<h1 className="onboarding-step-one__title">
						{isStudioAccount ? (
							<>
								What Best Describes Your
								<br />
								Role At The Studio?
							</>
						) : (
							'What Best Describes You?'
						)}
					</h1>

					<div className="onboarding-step-one__cards">
						{roleOptions.map(option => {
							const isSelected = selectedRole === option.id;
							return (
								<button
									key={option.id}
									type="button"
									className={`onboarding-step-one__card ${isStudioAccount ? 'is-studio ' : ''}${isSelected ? 'is-selected' : ''}`}
									onClick={() => setSelectedRole(option.id)}
								>
									{isStudioAccount ? (
										<>
											<p className="onboarding-step-one__studio-card-label">{option.label}</p>
											<span className={`onboarding-step-one__studio-card-dot ${isSelected ? 'is-selected' : ''}`}>
												{isSelected ? <CheckIcon /> : null}
											</span>
										</>
									) : (
										<>
											<div className="onboarding-step-one__card-image-wrap">
												<img src={option.image} alt={option.label} className="onboarding-step-one__card-image" />
											</div>
											<div className="onboarding-step-one__card-footer">
												<p>{option.label}</p>
												{isSelected ? <CheckCircleIcon /> : null}
											</div>
										</>
									)}
								</button>
							);
						})}
					</div>
				</main>

				<footer className="onboarding-step-one__footer">
					<button type="button" onClick={handleContinue} className={`onboarding-step-one__continue-btn ${!selectedRole ? 'is-disabled' : ''}`} disabled={!selectedRole}>
						Continue <EastIcon />
					</button>
					<button type="button" onClick={handleSkip} className="onboarding-step-one__skip-btn">
						Skip
					</button>
				</footer>
			</div>
		</div>
	);
};

export default Step1;
